import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PrimaryTextField from "../../component/PrimaryTextField";
import PrimaryButton from "../../component/PrimaryButton";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import UnAuthedLayout from "../../component/UnAuthedLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../redux/slices/loaderSlice";
import { resendActivationLink, setPassword } from "../../redux/thunk/userThunk";
import { showToaster } from "../../redux/slices/toastSlice";
import { DisplayErrorMessages } from "../../utils/helper";
import { palette } from "../../themes/theme";

const passwordCriteria = [
  {
    id: "length",
    text: "At least 8 characters",
    validate: (value) => value.length >= 8,
  },
  {
    id: "uppercase",
    text: "One uppercase letter",
    validate: (value) => /[A-Z]/.test(value),
  },
  {
    id: "lowercase",
    text: "One lowercase letter",
    validate: (value) => /[a-z]/.test(value),
  },
  { id: "number", text: "One number", validate: (value) => /\d/.test(value) },
  {
    id: "special",
    text: "One special character",
    validate: (value) => /[!@#$%^&*]/.test(value),
  },
];

// Yup validation schema
const validationSchema = Yup.object({
  password: Yup.string()
    .required("Password is required")
    .test(
      "password-valid",
      "Password must meet all the requirements",
      (value) =>
        passwordCriteria.every((criteria) => criteria.validate(value || ""))
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
});

const SetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState();
  const [getNewLink, setGetNewLink] = useState(false);

  const handleFormSubmit = async (values) => {
    const passwordAccessToken = searchParams.get("code");
    const resendLink = searchParams.get("resendLink");
    setLoading(true);
    try {
      const res = await dispatch(
        setPassword({
          code: passwordAccessToken,
          password: values.password,
          resendLink: Boolean(resendLink),
        })
      ).unwrap();
      if (res.data?.resendLink === true) {
        setGetNewLink(true);
      } else {
        navigate("sign-in");
      }
    } catch (err) {
      if (err.response?.data?.data?.resendLink === true) {
        setGetNewLink(true);
      }
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const checkTokenExpiry = async (token, resendLink) => {
    if (token) {
      dispatch(
        showLoader({
          showLoader: true,
          loaderMessage: "Please wait ....",
        })
      );
      try {
        await dispatch(
          setPassword({
            code: token,
            resendLink: resendLink,
          })
        ).unwrap();
        dispatch(hideLoader());
      } catch (err) {
        if (err.response?.data?.data?.resendLink === true) {
          setGetNewLink(true);
        } else {
          const error = DisplayErrorMessages(err);
          dispatch(
            showToaster({
              dialogBgColor: "bg-danger",
              dialogMsg: error ?? "Error",
              showDialog: true,
              timer: "5000",
            })
          );
          navigate("/sign-in");
        }
        dispatch(hideLoader());
      } finally {
        setLoading(false);
      }
    } else {
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: "Please contact the admin.....",
          showDialog: true,
          timer: "5000",
        })
      );
      navigate("/sign-in");
    }
  };

  const getNewLinkApi = async () => {
    const passwordAccessToken = searchParams.get("code");
    const resendLink = searchParams.get("resendLink");
    setLoading(true);
    try {
      await dispatch(
        resendActivationLink({
          code: passwordAccessToken,
          resendLink: Boolean(resendLink),
        })
      ).unwrap();
      navigate("sign-in");
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  const isPasswordValid = (criteriaId) =>
    passwordCriteria
      .find((criteria) => criteria.id === criteriaId)
      ?.validate(formik.values.password);

  useEffect(() => {
    const passwordAccessToken = searchParams.get("code");
    const resendLink = searchParams.get("resendLink");
    checkTokenExpiry(passwordAccessToken, Boolean(resendLink));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UnAuthedLayout showLoader={false}>
      {getNewLink ? (
        <Box>
          <Typography sx={{ color: palette.text.dark, textAlign: "center" }}>
            The activation link that was sent to you has expired. Please click
            'Resend Link' to receive a new one.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ marginTop: "16px", fontWeight: "bold", color: palette.text.white }}
              loading={loading}
              onClick={getNewLinkApi}
            >
              Resend Link
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              sx={{ marginTop: "16px", fontWeight: "bold", color: palette.primary.main }}
              onClick={() => navigate("/sign-in")}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{minWidth: "365px", maxWidth: "500px", margin: "0 auto"}}>
          <Typography
            variant="h5"
            gutterBottom
            color={palette.text.dark}
            fontWeight="bolder"
          >
            Create Your Password
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <Box mt={2}>
              <PrimaryTextField
                label="Password"
                name="password"
                type="password"
                fullWidth
                margin="normal"
                required
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
              />
              {/* Show password criteria only when field is dirty */}
              {formik.touched.password && (
                <Box mt={2}>
                  {passwordCriteria.map((criteria) => (
                    <Box
                      key={criteria.id}
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      {isPasswordValid(criteria.id) ? (
                        <CheckCircleOutlineIcon
                          color="primary"
                          fontSize="small"
                        />
                      ) : (
                        <CancelOutlinedIcon color="error" fontSize="small" />
                      )}
                      <Typography
                        variant="body2"
                        color={
                          isPasswordValid(criteria.id)
                            ? "primary.main"
                            : "error.main"
                        }
                      >
                        {criteria.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <PrimaryTextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                fullWidth
                margin="normal"
                required
                variant="outlined"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              sx={{ marginTop: "16px", fontWeight: "bold" }}
              loading={loading}
              disabled={!formik.isValid || !formik.dirty}
            >
              Submit
            </Button>
          </form>
        </Box>
      )}
    </UnAuthedLayout>
  );
};

export default SetPassword;
